import { setTabIndex } from './tabindex';

export default class MobileMenu {
  constructor() {
    this.activePanels = [];
    this.activeCollapses = [];
    this.lastInputType = null; // 'click' or 'keydown'
    this.lastTopLevelItems = []; // The depth-0 entries of the menu, see getTopLevelItems().
    this.toggles = [];

    // Define selectors for focusable elements within panels.
    this.focusableSelectors = [
      '.denhaag-mobile-menu__link',
      '[data-toggle-menu-panel]',
      'a[href]',
      'button:not([disabled])',
      '[tabindex="0"]',
      'select'
    ];

    this.mobileMenu = document?.getElementById( "denhaag-mobile-menu" );
    this.header = document?.getElementById( "site-header" );
    this.menuToggle = document.getElementById( "denhaag-menu-toggle" );

    if (!this.isEnabled()) {
      // Nothing to see here!
      return;
    }

    this.onEscapeMainMenuArray = [];
    this._isOpen = false;

    /** ClassNames */
    this.collapseActiveClass = "denhaag-mobile-menu__details--open";
    this.panelActiveClass = "denhaag-mobile-menu__panel--active";
    this.bodyMenuActiveClass = "denhaag-body--panel-active";
    this.bodyMobileMenuActiveClass = "denhaag-body--mobile-menu-active";
    this.buttonSecondaryAction = "denhaag-button--secondary-action";
    this.headerPanelActive = "denhaag-header__panel--open";
    this.mobileMenuStickyActiveClass = "denhaag-mobile-menu--is-sticky";

    /** Elements */
    this.backButtons = document.getElementsByClassName( "denhaag-mobile-menu__button-back" );
    this.headerSecondaryMenu = document.querySelector( ".denhaag-header__secondary" );

    this.panels = [...this.mobileMenu?.getElementsByClassName( "js-mobile-menu-panel" ), ...this.header?.getElementsByClassName( "js-mobile-menu-panel" )];
    this.collapses = [...this.mobileMenu?.querySelectorAll( ".denhaag-mobile-menu__details" ), ...this.header?.querySelectorAll( ".denhaag-mobile-menu__details" )];

    this.mobileLoginButton = this.headerSecondaryMenu?.querySelector( ".mobile-login-button" );
  }

  initialize() {
    if (!this.isEnabled()) {
      return;
    }

    // Keep track of the last input type (keydown or click), don`t use `event.preventDefault()` here.
    document.addEventListener( 'keydown', () => this.lastInputType = 'keydown' );
    document.addEventListener( 'click', () => this.lastInputType = 'click' );

    this.toggles = [...this.mobileMenu?.querySelectorAll( "[data-toggle-menu-panel]" ), ...this.header?.querySelectorAll( "[data-toggle-menu-panel]" )];

    this.lastTopLevelItems = this.getTopLevelItems();

    /**
     * EVENTS:
     */
    this.menuToggle.onclick = () => this.dispatchEvent( "mobileMenu:toggle", {from: "headerMenuToggle"} );
    this.collapses?.forEach( collapse => collapse.querySelector( ':scope > button:first-of-type' ).addEventListener( 'click', () => this.toggleCollapse( collapse ) ) );

    // Close menu on overlay click.
    document.getElementById( "denhaag-mobile-menu__overlay" ).onclick = () => this.dispatchEvent( "mobileMenu:close" );
    this.toggles?.forEach( toggle => toggle.addEventListener( 'click', (e) => {
      e.preventDefault();
      this.dispatchEvent( "mobileMenu:togglePanel", {id: toggle?.getAttribute( 'data-toggle-menu-panel' )} );
    } ) );

    //
    this.menuToggle?.addEventListener( 'keydown', (e) => {
      const {key} = e;

      switch (key) {
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'Escape':
          e.preventDefault();
          this.closeMobileMenu();
          break;

        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          this.openMobileMenu( {id: this.toggles[0]?.getAttribute( 'data-toggle-menu-panel' ), from: "headerMenuToggle"} );
          break;
      }
    } );

    this.mobileMenu?.addEventListener( 'keydown', (e) => {
      const {key} = e;

      switch (key) {
        case 'ArrowDown':
          // Navigate within panel.
          if (this.activePanels.length && document.activeElement === this.getLastFocusableChild( this.activePanels[this.activePanels.length - 1] )) {
            // Focus first item if last item is focused.
            e.preventDefault();
            this.setFocus( this.getFirstFocusableChild( this.activePanels[this.activePanels.length - 1] ) );
          } else if (this.activePanels.length && document.activeElement !== this.getLastFocusableChild( this.activePanels[this.activePanels.length - 1] )) {
            // Focus next item if not last item.
            e.preventDefault();

            const currentLevelItems = this.getChildren( this.activePanels[this.activePanels.length - 1] );
            const currentIndex = Array.from( currentLevelItems ).indexOf( document.activeElement );
            if (currentIndex > -1 && currentIndex < (currentLevelItems.length - 1)) {
              this.setFocus( currentLevelItems[currentIndex + 1] );
            }

          } else if (this.activeCollapses.length) {
            // Focus first item if last item is focused.
            e.preventDefault();

            const currentLevelItems = this.getChildren( this.activeCollapses[this.activeCollapses.length - 1] );
            const currentIndex = Array.from( currentLevelItems ).indexOf( document.activeElement );
            if (currentIndex > -1 && currentIndex < (currentLevelItems.length - 1)) {
              this.setFocus( currentLevelItems[currentIndex + 1] );
            } else if (currentIndex === (currentLevelItems.length - 1)) {
              // This will close the collapse and focus the toggle button.
              this.dispatchEvent( "mobileMenu:closeCollapse", {id: this.activeCollapses[this.activeCollapses.length - 1]} );
            }
          } else {
            const currentLevelItems = this.getChildren( this.mobileMenu );
            const currentIndex = Array.from( currentLevelItems ).indexOf( document.activeElement );

            if (currentIndex > -1 && currentIndex < (currentLevelItems.length - 1)) {
              this.setFocus( currentLevelItems[currentIndex + 1] );
            } else {
              // We're going to focus the next element, which is within another HTML element.
              let currentLevelItems = this.getChildren( this.mobileMenu ) || [];
              const topLevelElements = Array.from( this.mobileMenu.children );

              // Remove the first element, which is the mobileMenu.
              topLevelElements?.shift();
              topLevelElements?.forEach( parent => currentLevelItems.push( [...this.getFocusableElements( parent )] ) );

              currentLevelItems = currentLevelItems.flat();

              const currentIndex = Array.from( currentLevelItems ).indexOf( document.activeElement );
              if (currentIndex > -1 && currentIndex < (currentLevelItems.length - 1)) {
                this.setFocus( currentLevelItems[currentIndex + 1] );
              } else if (currentIndex === (currentLevelItems.length - 1)) {
                this.setFocus( currentLevelItems[0] );
              }
            }
          }

          break;
        case 'ArrowUp':
          // Navigate within panel (reverse order).
          if (this.activePanels.length && document.activeElement === this.getFirstFocusableChild( this.activePanels[this.activePanels.length - 1] )) {
            // Focus last item if first item is focused.
            e.preventDefault();
            this.setFocus( this.getLastFocusableChild( this.activePanels[this.activePanels.length - 1] ) );
          } else if (this.activePanels.length && document.activeElement !== this.getFirstFocusableChild( this.activePanels[this.activePanels.length - 1] )) {
            // Focus previous item if not first item.
            e.preventDefault();

            const currentLevelItems = this.getChildren( this.activePanels[this.activePanels.length - 1] );
            const currentIndex = Array.from( currentLevelItems ).indexOf( document.activeElement );
            if (currentIndex > 0) {
              this.setFocus( currentLevelItems[currentIndex - 1] );
            }

          } else if (this.activeCollapses.length) {
            // Focus previous item in collapse.
            e.preventDefault();

            const currentLevelItems = this.getChildren( this.activeCollapses[this.activeCollapses.length - 1] );
            const currentIndex = Array.from( currentLevelItems ).indexOf( document.activeElement );
            if (currentIndex > 0) {
              this.setFocus( currentLevelItems[currentIndex - 1] );
            } else if (currentIndex === 0) {
              // This will close the collapse and focus the toggle button.
              this.dispatchEvent( "mobileMenu:closeCollapse", {id: this.activeCollapses[this.activeCollapses.length - 1]} );
            }
          } else {
            const currentLevelItems = this.getChildren( this.mobileMenu );
            const currentIndex = Array.from( currentLevelItems ).indexOf( document.activeElement );

            if (currentIndex > 0) {
              this.setFocus( currentLevelItems[currentIndex - 1] );
            } else {
              // We're going to focus the previous element, which might be within another HTML element.
              let currentLevelItems = this.getChildren( this.mobileMenu ) || [];
              const topLevelElements = Array.from( this.mobileMenu.children );

              // Remove the first element, which is the overlay.
              topLevelElements?.shift();
              topLevelElements?.forEach( parent => currentLevelItems.push( [...this.getFocusableElements( parent )] ) );

              currentLevelItems = currentLevelItems.flat();

              const currentIndex = Array.from( currentLevelItems ).indexOf( document.activeElement );
              if (currentIndex > 0) {
                this.setFocus( currentLevelItems[currentIndex - 1] );
              } else if (currentIndex === 0) {
                // Loop to last item
                this.setFocus( currentLevelItems[currentLevelItems.length - 1] );
              }
            }
          }
          break;

        case 'ArrowRight':
          if (this.toggles.includes( document.activeElement )) {
            // Open panel
            this.dispatchEvent( "mobileMenu:openPanel", {id: document.activeElement.getAttribute( 'data-toggle-menu-panel' )} );
          } else if ('BUTTON' === document.activeElement.tagName) {
            // Open collapse.
            this.dispatchEvent( "mobileMenu:openCollapse", {id: document.activeElement.parentNode?.id} );
          }
          break;

        case 'ArrowLeft':
        case 'Escape':
          if (this.activePanels.length) {
            // Close last-active collapse on Escape key press.
            e.preventDefault();
            this.dispatchEvent( "mobileMenu:closePanel", {id: this.activePanels[this.activePanels.length - 1]} );
          } else if (this.activeCollapses.length) {
            // Close last-active collapse on Escape key press.
            e.preventDefault();
            this.dispatchEvent( "mobileMenu:closeCollapse", {id: this.activeCollapses[this.activeCollapses.length - 1]} );

          } else if (this.isOpen) {
            // Close entire mobile menu on Escape key press.
            e.preventDefault();
            this.dispatchEvent( "mobileMenu:close" );
          }
          break;

        case 'Tab':
          if (e.shiftKey) {
            // Going backwards.
            if (this.activePanels.length && document.activeElement === this.getFirstFocusableChild( this.activePanels[this.activePanels.length - 1] )) {
              e.preventDefault();
              this.setFocus( this.getLastFocusableChild( this.activePanels[this.activePanels.length - 1] ) );
            } else if (this.activeCollapses.length && document.activeElement === this.getFirstFocusableChild( this.activeCollapses[this.activeCollapses.length - 1] )) {
              e.preventDefault();
              this.setFocus( this.getLastFocusableChild( this.activeCollapses[this.activeCollapses.length - 1] ) );
            } else if (this.isOpen && document.activeElement === (this.getFirstFocusableChild( this.mobileMenu ))) {
              e.preventDefault();
              this.setFocus( this.getLastFocusableChild( this.mobileMenu ) );
            }
          } else {
            // Going forwards.
            if (this.activePanels.length && document.activeElement === this.getLastFocusableChild( this.activePanels[this.activePanels.length - 1] )) {
              e.preventDefault();
              this.setFocus( this.getFirstFocusableChild( this.activePanels[this.activePanels.length - 1] ) );
            } else if (this.activeCollapses.length && document.activeElement === this.getLastFocusableChild( this.activeCollapses[this.activeCollapses.length - 1] )) {
              e.preventDefault();
              this.setFocus( this.getFirstFocusableChild( this.activeCollapses[this.activeCollapses.length - 1] ) );
            } else if (this.isOpen) {
              if (document.activeElement === (this.getLastFocusableChild( this.mobileMenu ) ?? this.lastTopLevelItems[this.lastTopLevelItems.length - 1])) {
                e.preventDefault();
                this.setFocus( this.getFirstFocusableChild( this.mobileMenu ) );
              }
            }
          }
          break;
      }
    } );

    this.toggleTabindex();
    this.resize();
  }

  /**
   * Check if the component can be used.
   *
   * @return boolean
   */
  isEnabled() {
    return !!(this.mobileMenu && this.header && this.menuToggle);
  }

  // Getter for isOpen
  get isOpen() {
    return this._isOpen;
  }

  // Setter for isOpen - handles scroll disable/enable automatically
  set isOpen(value) {
    if (this._isOpen !== value) {
      this._isOpen = value;

      if (!this._isOpen) {
        document.body.style.overflow = "";
      } else {
        document.body.style.overflow = "hidden";
      }
    }
  }

  setupEventListeners() {
    window.addEventListener( "mobileMenu:toggle", (e) => this.toggleMobileMenu( e.detail ) );
    window.addEventListener( "mobileMenu:open", (e) => this.openMobileMenu( e.detail ) );
    window.addEventListener( "mobileMenu:close", () => this.closeMobileMenu() );

    window.addEventListener( "mobileMenu:togglePanel", (e) => this.togglePanel( e.detail?.id ) );
    window.addEventListener( "mobileMenu:closePanel", (e) => this.closePanel( e.detail?.id ) );
    window.addEventListener( "mobileMenu:openPanel", (e) => this.openPanel( e.detail?.id ) );
    window.addEventListener( 'mobileMenu:toggleCollapse', (e) => this.toggleCollapse( e.detail?.id ) );
    window.addEventListener( 'mobileMenu:closeCollapse', (e) => this.closeCollapse( e.detail?.id ) );
    window.addEventListener( 'mobileMenu:openCollapse', (e) => this.openCollapse( e.detail?.id ) );
  }

  dispatchEvent(name, data = {}) {
    if (!name) {
      return;
    }

    window.dispatchEvent( new CustomEvent( name, {
      detail: data,
      bubbles: true
    } ) );
  }

  toggleMobileMenu(data) {
    !this.isOpen ?
      this.dispatchEvent( "mobileMenu:open", data ) :
      this.dispatchEvent( "mobileMenu:close", data );
  }

  /**
   * Open Mobile Menu.
   * @param {object} data The data object containing the ID and source of the request.
   */
  openMobileMenu(data) {
    const {id, from} = data || {};

    // Close any open root-level header panels (search, language) before opening mobile menu.
    document.querySelectorAll( '[data-panel-depth="root"]' )?.forEach( panel => {
      if (panel.id) {
        this.dispatchEvent( "panel:close", {id: panel.id} );
      }
    } );

    // Add standard body classes.
    document.body.classList.add( this.bodyMenuActiveClass );
    document.body.classList.add( this.bodyMobileMenuActiveClass );

    // Handle main menu toggle
    if (this.menuToggle) {
      this.menuToggle.classList.add( this.buttonSecondaryAction );
      this.menuToggle.setAttribute( "aria-expanded", "true" );
      this.menuToggle.firstElementChild.setAttribute( "aria-hidden", "true" );
      this.menuToggle.lastElementChild.removeAttribute( "aria-hidden" );
    }

    // Add standard header panel active class.
    this.mobileMenu?.classList.add( this.headerPanelActive );

    // If opened from sticky subnav, add sticky active class
    if (from === "stickySubNavMobile") {
      this.mobileMenu?.classList.add( this.mobileMenuStickyActiveClass );
    }

    this.isOpen = true;
    this.openPanel( id );

    // Get top-level-items.
    setTabIndex( this.lastTopLevelItems, 0 );
  }

  closeMobileMenu() {
    // Remove standard body classes
    document.body.classList.remove( this.bodyMenuActiveClass );
    document.body.classList.remove( this.bodyMobileMenuActiveClass );

    // Handle main menu toggle
    if (this.menuToggle) {
      this.menuToggle.classList.remove( this.buttonSecondaryAction );
      this.menuToggle.setAttribute( "aria-expanded", "false" );
      this.menuToggle.firstElementChild.removeAttribute( "aria-hidden" );
      this.menuToggle.lastElementChild.setAttribute( "aria-hidden", "true" );
    }

    // Remove standard header panel active class
    this.mobileMenu?.classList.remove( this.headerPanelActive );

    // Always remove sticky active class when closing the menu
    this.mobileMenu?.classList.remove( this.mobileMenuStickyActiveClass );

    this.forceClose();
    this.isOpen = false;

    setTabIndex( this.lastTopLevelItems, -1 );

    if (this.lastInputType === 'keydown') {
      this.menuToggle?.focus();
    }

    this.dispatchEvent( "stickySubNavMobile:close" );
  }

  // onclickBackButton
  onclickBackButton(buttonSelector) {
    const parentMenuButton = buttonSelector.closest( ".menu-item-has-children" )?.childNodes[0]; // backButton.closest('.denhaag-mobile-menu__link') does not work

    if (parentMenuButton.hasAttribute( "aria-expanded" )) {
      parentMenuButton.setAttribute( "aria-expanded", "false" );
    }

    buttonSelector.closest( ".js-mobile-menu-panel" ).classList.toggle( this.panelActiveClass );
    parentMenuButton.focus();
  }

  /**
   * Force close open Panels.
   */
  forceClose() {
    this.activePanels?.forEach( panel => this.setPanelInactive( panel ) );
    this.activeCollapses?.forEach( c => this.closeCollapse( c ) );
    this.activePanels = [];
  }

  /**
   * Toggle Collapses.
   *
   * @param collapse The collapse element.
   */
  toggleCollapse(collapse) {
    const collapseIsOpen = this.activePanels.length || this.isInActiveCollapses( collapse?.getAttribute( 'id' ) );
    !collapseIsOpen ? this.openCollapse( collapse ) : this.closeCollapse( collapse );
  }

  /**
   * Open Collapse.
   * @param {HTMLElement|string} collapse The collapse element or its ID.
   */
  openCollapse(collapse) {
    if (typeof collapse === 'string') {
      collapse = document.getElementById( collapse );
    }

    if (!this.activeCollapses.includes( collapse )) {
      this.activeCollapses.push( collapse );
    }
    collapse.classList.add( this.collapseActiveClass );

    // Update aria-expanded on the toggle button.
    const toggleButton = collapse.querySelector( ':scope > button[aria-expanded]' );
    if (toggleButton) {
      toggleButton.setAttribute( 'aria-expanded', 'true' );
    }

    this.enableOrDisableDirectCollapseChildren( collapse, true );
  }

  /**
   * Close Collapse.
   * @param {HTMLElement|string} collapse The collapse element or its ID.
   */
  closeCollapse(collapse) {
    if (typeof collapse === 'string') {
      collapse = document.getElementById( collapse );
    }

    this.activeCollapses = this.activeCollapses?.filter( c => c !== collapse );
    collapse.classList.remove( this.collapseActiveClass );

    // Update aria-expanded on the toggle button.
    const toggleButton = collapse.querySelector( ':scope > button[aria-expanded]' );
    if (toggleButton) {
      toggleButton.setAttribute( 'aria-expanded', 'false' );
    }

    this.enableOrDisableDirectCollapseChildren( collapse, false );
    this.setFocus( this.getFirstFocusableChild( collapse ) );
  }

  /**
   * Enable or disable direct children of a collapse.
   * @param {HTMLElement} collapse The collapse element.
   * @param {boolean} enable True to enable, false to disable.
   */
  enableOrDisableDirectCollapseChildren(collapse, enable = true) {
    const elementsToMakeClickable = [];
    [...collapse.querySelector( '.denhaag-mobile-menu__details-content' ).children]?.forEach( item => elementsToMakeClickable.push( ...this.getClickableElements( item, true ) ) );

    setTabIndex( elementsToMakeClickable, enable ? 0 : -1 );
  }

  /**
   * Set focus on specific element.
   * @param {object} element HTML object.
   */
  setFocus(element) {
    if (!element) {
      return;
    }
    element.focus();
  }

  /**
   * Toggle Panel.
   * @param {string} id ID of the element.
   */
  togglePanel(id) {
    if (!id) {
      return;
    }

    this.dispatchEvent(
      this.isInActivePanels( id )
        ? 'mobileMenu:closePanel'
        : 'mobileMenu:openPanel'
      , {
        id
      } );
  }

  /**
   * Check if a panel is in the activePanels array.
   *
   * @param {string} id The panel ID to check.
   * @return {boolean}
   */
  isInActivePanels(id) {
    return this.activePanels?.some( panel => panel.getAttribute( 'id' ) === id ) || false;
  }

  /**
   * Check if a collapse is in the activeCollapses array.
   *
   * @param id
   * @return {boolean}
   */
  isInActiveCollapses(id) {
    return this.activeCollapses?.some( panel => panel.getAttribute( 'id' ) === id ) || false;
  }

  /**
   * Open Panel.
   *
   * @param {string} id The panel ID to open.
   */
  openPanel(id) {
    const panel = document.getElementById( id );
    if (!panel) {
      return;
    }

    // Loop through each panel of this.panels and check if the panel with `id` is a child of any other panel.
    this.activePanels = this.panels.filter( panelItem => panelItem !== panel && panelItem.contains( panel ) );
    if (this.activePanels.indexOf( panel ) === -1) {
      this.activePanels.push( panel );
    }

    // Panels may be in a collapse, so we need to find the collapses which contain the active panels.
    this.activeCollapses = this.collapses?.filter( c => c.contains( panel ) && c !== panel );

    // Update all active panels and collapses.
    this.activeCollapses?.forEach( c => this.openCollapse( c ) );

    this.activePanels?.forEach( (p, i) => this.setPanelActive( p ) );

    this.setFocus( this.getFirstFocusableChild( panel ) );
  }

  /**
   * Close Panel.
   * @param {string|HTMLElement} id The panel ID to close.
   */
  closePanel(id) {
    let panel = id;
    if (typeof id === 'string') {
      panel = document.getElementById( id );
    }

    if (!panel) {
      return;
    }

    // Remove the panel from the activePanels array.
    this.activePanels = this.activePanels?.filter( p => p !== panel );
    this.setPanelInactive( panel );

    // Close the containing collapse (updates aria-expanded on toggle button).
    const containingCollapse = panel.closest( '.denhaag-mobile-menu__details' );
    if (containingCollapse && this.activeCollapses.includes( containingCollapse )) {
      this.closeCollapse( containingCollapse );
    }

    // Focus on the first focusable child or the previous sibling (usually the toggle button).
    this.setFocus( this.getFirstFocusableChild( panel ) ?? panel.previousElementSibling );
  }

  /**
   * Set a panel active.
   * @param {HTMLElement} panel The panel to set active.
   */
  setPanelActive(panel) {
    panel.classList.add( this.panelActiveClass )
    const itemsToMakeClickable = [];
    const list = panel.querySelector( '.denhaag-mobile-menu__panel-list' );
    list?.children && [...list.children]?.forEach( item => this.getClickableElements( item, true ).forEach( el => itemsToMakeClickable.push( el ) ) );

    setTabIndex( [...this.getClickableElements( panel, true ), ...itemsToMakeClickable], 0 );
  }

  /**
   * Get the depth-0 entries of the mobile menu.
   *
   * DHNL-6: this used to read `this.mobileMenu.lastElementChild`, which silently meant two
   * different things — the login-button container when one is configured, and otherwise the
   * whole menu list, in which case every nested link counted as "top level" and got made
   * tabbable while its submenu was still closed. Query the depth-0 entries explicitly
   * instead. Deliberately not filtered on visibility: this runs at DOMContentLoaded, when
   * the menu is still hidden on desktop and every element would measure 0x0.
   *
   * @return {HTMLElement[]} The depth-0 links, panel toggles and the optional login button.
   */
  getTopLevelItems() {
    const list = this.mobileMenu?.querySelector( '.denhaag-mobile-menu__list' );

    return [
      // A depth-0 entry is either a plain `<li><a>` or, when it has children,
      // `<li><div class="denhaag-mobile-menu__details"><button>` (see NLDS_Mobile_Walker).
      ...(list?.querySelectorAll( ':scope > li > a, :scope > li > .denhaag-mobile-menu__details > button' ) || []),
      ...(this.mobileMenu?.querySelectorAll( '.denhaag-mobile-menu__login-button' ) || []),
    ];
  }

  /**
   * Get all clickable elements within a panel.
   * @param {HTMLElement} parent The parent element to search within.
   * @param {boolean} directChildren If true, only get direct children (not used currently).
   * @return {*[]}
   */
  getClickableElements(parent, directChildren = false) {
    if (!parent || !parent.querySelectorAll) {
      return [];
    }

    if (directChildren) {
      return [...parent.querySelectorAll( ':scope > A, :scope > BUTTON' )];
    }

    return [
      ...new Set( [
        ...parent.getElementsByTagName( "A" ),
        ...parent.getElementsByTagName( "BUTTON" ),
        ...this.getChildren( parent )
      ] )
    ];
  }

  /**
   * Get all direct children which are clickable elements.
   *
   * @param {HTMLElement} parent
   * @return {*}
   */
  getChildren(parent) {
    return [...parent?.querySelectorAll( ':scope>button, :scope>a, :scope>ul>li>a, :scope>ul>li>button, :scope>ul>li>div>button' )]?.filter( el => this.isElementFocusable( el ) );
  }

  /**
   * Set a panel inactive.
   * @param {object} panel The panel to set inactive.
   */
  setPanelInactive(panel) {
    panel.classList.remove( this.panelActiveClass, 'is-active' );
    setTabIndex( this.getClickableElements( panel ), -1 );
  }

  toggleTabindex = () => setTabIndex( [this.mobileLoginButton].filter( Boolean ), window.innerWidth >= 1024 ? -1 : 0 );

  resize() {
    this.toggleTabindex();

    if (!this.menuToggle) {
      return;
    }

    if (!this.isElementFocusable( this.menuToggle ) && this.isOpen) {
      this.closeMobileMenu()
    }
  }

  /**
   * Check if an element is focusable.
   *
   * @param {HTMLElement} element The element to check.
   * @return {boolean} True if the element is focusable, false otherwise.
   */
  isElementFocusable(element) {
    // Check if element is visible and not disabled
    const style = window.getComputedStyle( element );

    return (
      element.offsetWidth > 0 &&
      element.offsetHeight > 0 &&
      style.visibility !== 'hidden' &&
      style.display !== 'none' &&
      !element.hasAttribute( 'disabled' ) &&
      element.tabIndex !== -1
    );
  }

  /**
   * Get the first focusable child element within a parent element.
   * @param {HTMLElement} parent The parent element to search within.
   * @return {HTMLElement|null} The first focusable child element, or null if none found.
   */
  getFirstFocusableChild(parent) {
    if (!parent) {
      return null;
    }

    const elements = this.getFocusableElements( parent );

    if (!elements?.length) {
      return null;
    }

    for (const element of elements) {
      if (this.isElementFocusable( element )) {
        return element;
      }
    }

    return null;
  }

  /**
   * Get the last focusable child element within a parent element.
   * @param {HTMLElement} parent The parent element to search within.
   * @return {HTMLElement|null} The last focusable child element, or null if none found.
   */
  getLastFocusableChild(parent) {
    if (!parent) {
      return null;
    }

    const elements = this.getFocusableElements( parent );

    if (!elements?.length) {
      return null;
    }

    // WARNING: Iterate in reverse order to find the last focusable element.
    for (let i = elements.length - 1; i >= 0; i--) {
      if (this.isElementFocusable( elements[i] )) {
        return elements[i];
      }
    }

    return null;
  }

  /**
   * Get all focusable elements within a parent element.
   *
   * @param parent
   * @return {*|*[]}
   */
  getFocusableElements(parent) {
    if (!parent) {
      return [];
    }

    return parent?.querySelectorAll( this.focusableSelectors.join( ',' ) ) || []
  }
}
